import { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import { LoadingState } from '@/components/ui/loading-state';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { AlertCircle, CheckCircle } from 'lucide-react';
import { subscribeToNewsletterDirectly } from '@/services/emailSubscriptionService';
import { parseFullName } from '@/lib/nameUtils';
import { useAuthCallbackDeepLink } from '@/hooks/useDeepLinkHandler';
import { isIOSNativeApp, isAndroidNativeApp } from '@/utils/platformUtils';
import type { Session } from '@supabase/supabase-js';

export default function AuthCallback() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const processedRef = useRef(false);
  const isNativeApp = isIOSNativeApp() || isAndroidNativeApp();

  // Note: Manual email verification logic removed
  // Email verification is now handled by the EmailCallback bridge page
  // This component now focuses on hash-based auth flows (social login, etc.)

  // Handle deep link auth callbacks on native apps
  useAuthCallbackDeepLink((event) => {
    console.log('🔗 Received auth callback deep link:', event);

    // Extract auth parameters from deep link
    const { params } = event;

    // Convert deep link params to URL search params for processing
    const urlParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      urlParams.set(key, value);
    });

    // Update the current URL with the deep link parameters
    const newUrl = `${window.location.pathname}?${urlParams.toString()}`;

    // If there are auth tokens in the params, add them to the hash for Supabase
    const hasAuthTokens = params.access_token || params.refresh_token;
    if (hasAuthTokens) {
      const hashParams = new URLSearchParams();
      if (params.access_token) hashParams.set('access_token', params.access_token);
      if (params.refresh_token) hashParams.set('refresh_token', params.refresh_token);
      if (params.token_type) hashParams.set('token_type', params.token_type);
      if (params.expires_in) hashParams.set('expires_in', params.expires_in);

      window.history.replaceState(null, '', `${newUrl}#${hashParams.toString()}`);
    } else {
      window.history.replaceState(null, '', newUrl);
    }

    // Don't force reload - let the useEffect handle the processing
    // window.location.reload(); // REMOVED: Let useEffect process the auth callback
  }, isNativeApp);

  useEffect(() => {
    // Log all available data for debugging
    console.log('🔐 AuthCallback useEffect triggered');
    console.log('🔐 Current URL:', window.location.href);
    console.log('🔐 Search params:', Object.fromEntries(searchParams.entries()));
    console.log('🔐 Hash fragment:', window.location.hash);
    console.log('🔐 Platform:', isNativeApp ? 'native' : 'web');

    // Check for error in URL params first
    const error = searchParams.get('error');
    const errorDescription = searchParams.get('error_description');

    if (error) {
      console.error('Auth callback error:', error, errorDescription);
      setErrorMessage(errorDescription || error);
      setStatus('error');
      return;
    }

    // This component now only handles hash-based auth flows (social login, etc.)
    // Email verification is handled by the EmailCallback bridge page

    // Check for hash fragment that contains auth params
    const hashFragment = window.location.hash;
    if (!hashFragment || !hashFragment.includes('access_token')) {
      console.error('🔐 No auth hash fragment found - this may be an email verification callback');
      setErrorMessage('This appears to be an email verification link. Please use the link from your email.');
      setStatus('error');
      return;
    }

    console.log('🔐 Processing hash-based auth callback (social login, etc.)');

    // Set up auth state listener to handle session changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event: string, session: Session | null) => {


        // Only process SIGNED_IN events and avoid duplicate processing
        if (event === 'SIGNED_IN' && session?.user && !processedRef.current) {
          processedRef.current = true;



          // Clear the hash to prevent reprocessing
          window.history.replaceState(null, '', window.location.pathname);



          // Check if user opted for newsletter during signup
          const newsletterConsent = session.user.user_metadata?.newsletter_consent;

          // If user consented to newsletter, subscribe them directly
          if (newsletterConsent) {
            const userEmail = session.user.email;
            const fullName = session.user.user_metadata?.full_name;

            if (userEmail) {
              const { firstName, lastName } = parseFullName(fullName || '');
              subscribeToNewsletterDirectly(userEmail, firstName, lastName)
                .then((result) => {
                  if (result.error) {
                    console.error('Newsletter subscription failed:', result.error);
                    // Don't show error to user since account confirmation succeeded
                  } else {
                    console.log('Newsletter subscription successful');
                  }
                })
                .catch((error) => {
                  console.error('Newsletter subscription error:', error);
                  // Don't show error to user since account confirmation succeeded
                });
            }
          }

          setStatus('success');

          toast({
            title: 'Email confirmed!',
            description: newsletterConsent
              ? 'Your account has been verified and you\'ve been subscribed to our newsletter. You can now sign in.'
              : 'Your account has been verified. You can now sign in.',
          });

          // Redirect to login after a short delay
          setTimeout(() => {
            navigate('/auth/login', { replace: true });
          }, 2000);
        }
      }
    );



    // Cleanup function
    return () => {
      subscription.unsubscribe();
    };
  }, [navigate, searchParams, isNativeApp]);

  if (status === 'loading') {
    return (
      <LoadingState
        title="Confirming your email"
        description="Please wait while we verify your account..."
        fullScreen={true}
      />
    );
  }

  if (status === 'success') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8 text-center">
          <div>
            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100">
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
            <h2 className="mt-6 text-3xl font-bold tracking-tight text-gray-900">
              Email Confirmed!
            </h2>
            <p className="mt-2 text-sm text-gray-600">
              Your account has been successfully verified. You will be redirected to the login page shortly.
            </p>
          </div>
          <Button 
            onClick={() => navigate('/auth/login', { replace: true })}
            className="w-full"
          >
            Continue to Login
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-red-100">
            <AlertCircle className="h-8 w-8 text-red-600" />
          </div>
          <h2 className="mt-6 text-3xl font-bold tracking-tight text-gray-900">
            Confirmation Failed
          </h2>
        </div>
        
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            {errorMessage || 'There was a problem confirming your email address.'}
          </AlertDescription>
        </Alert>

        <div className="space-y-4">
          <Button 
            onClick={() => navigate('/auth/signup', { replace: true })}
            className="w-full"
          >
            Try Signing Up Again
          </Button>
          <Button 
            variant="outline"
            onClick={() => navigate('/auth/login', { replace: true })}
            className="w-full"
          >
            Back to Login
          </Button>
        </div>
      </div>
    </div>
  );
}
